/**
 * Session/auth domain: who is signed in, the AccountModal's open/mode state, the invite-link /
 * email-confirmation / password-reset / Stripe-return arrival flows, and resuming a signed-in
 * visitor back to exactly where they left off. Extracted out of App.tsx, which composes this
 * alongside the professional-portal and page-routing concerns.
 */
import { useState, useEffect, useRef, type Dispatch, type SetStateAction } from 'react';
import { setCurrentAdviceAttribution } from '../features/professionalPortal/adviceAttribution.ts';
import { rearmAdviserIntro } from '../components/narrative/index.tsx';
import { getSessionUser, signOut as authSignOut } from '../services/authClient.ts';
import { getSignInDoor, clearSignInDoor } from '../services/signInDoor.ts';
import type { SessionUser } from '../services/authClient.ts';
import { capturePendingInviteFromUrl, getPendingInvite, takePendingInvite, inviteInProgress, finishInviteRedemption, acceptInvite } from '../services/tenantApi.ts';
import {
  loadLatestClientProfile,
  clearSessionMirror,
  restoreSessionMirror,
  setStoredProfileId,
} from '../services/profileApi.ts';
import type { LoadedClientProfile, ProfileAppData, ProfileAppState } from '../services/profileApi.ts';
import { getEntitlement, consumeBillingReturn } from '../services/billingApi.ts';
import type { PartnerBillingSummary } from '../services/billingApi.ts';
import { getPartnerWizardDraft, clearPartnerWizardDraft } from '../features/professionalPortal/PartnerOnboardingWizard.tsx';
import { RESUMABLE_PAGES, resolveFirmMembershipStatus } from '../features/professionalPortal/useProfessionalPortal.ts';
import type { PartnerFirmState } from '../features/professionalPortal/useProfessionalPortal.ts';
import { INITIAL_FACT_FIND } from '../constants/index.ts';
import type { DraftFactFind } from '../constants/initialFactFind.ts';
import type { SavedStrategy, SavedCaseStudy } from '../pages/Dashboard/dashboardConfig.tsx';
import type { CoupleValidationState } from '../features/agedCare/AgedCareAnalyser.tsx';

export interface UseSessionAndAuthParams {
  accountUser: SessionUser | null;
  setAccountUser: Dispatch<SetStateAction<SessionUser | null>>;
  activePage: string;
  setActivePage: Dispatch<SetStateAction<string>>;
  setFactFind: Dispatch<SetStateAction<DraftFactFind>>;
  setClientProfileStep: Dispatch<SetStateAction<number>>;
  setSavedStrategies: Dispatch<SetStateAction<SavedStrategy[]>>;
  setSavedCaseStudies: Dispatch<SetStateAction<SavedCaseStudy[]>>;
  setCoupleValidation: Dispatch<SetStateAction<CoupleValidationState>>;
  setShowPaywall: Dispatch<SetStateAction<boolean>>;
  partnerIntent: boolean;
  setPartnerIntent: Dispatch<SetStateAction<boolean>>;
  setActiveProfessionalCaseId: Dispatch<SetStateAction<string | null>>;
  setActiveRealProfileId: Dispatch<SetStateAction<string | null>>;
  setPartnerFirm: Dispatch<SetStateAction<PartnerFirmState | null>>;
  setPartnerResolving: Dispatch<SetStateAction<boolean>>;
  setPortalRefreshNonce: Dispatch<SetStateAction<number>>;
  showPartnerWizard: boolean;
  setShowPartnerWizard: Dispatch<SetStateAction<boolean>>;
  setPartnerPaywall: Dispatch<SetStateAction<{ firmName: string; tenantId: string } | null>>;
  setAddClientPaywall: Dispatch<SetStateAction<{ summary: PartnerBillingSummary | null; tenantId: string } | null>>;
  /** Waits for any in-flight/queued autosave to settle before we invalidate the session — see
   *  useAutoSave.ts's flushPendingSave doc comment for why sign-out can otherwise lose data. */
  flushPendingSave: () => Promise<void>;
  /** Opens the admin console instead of onboarding when the server confirms a platform admin.
   *  Resolves true when it did. See App.tsx. */
  routePlatformAdminWithoutFirm: () => Promise<boolean>;
}

export function useSessionAndAuth({
  accountUser,
  setAccountUser,
  activePage,
  setActivePage,
  setFactFind,
  setClientProfileStep,
  setSavedStrategies,
  setSavedCaseStudies,
  setCoupleValidation,
  setShowPaywall,
  partnerIntent,
  setPartnerIntent,
  setActiveProfessionalCaseId,
  setActiveRealProfileId,
  setPartnerFirm,
  setPartnerResolving,
  setPortalRefreshNonce,
  showPartnerWizard,
  setShowPartnerWizard,
  setPartnerPaywall,
  setAddClientPaywall,
  flushPendingSave,
  routePlatformAdminWithoutFirm,
}: UseSessionAndAuthParams) {
  // 'reset' when the user arrives from a password-reset email link; null otherwise.
  const [accountModalMode, setAccountModalMode] = useState<string | null>(null);
  // The token from a ?reset=<token> URL, threaded into AccountModal for the reset-confirm call.
  const [resetToken, setResetToken] = useState<string | null>(null);
  const [showAccountModal, setShowAccountModal] = useState(false);
  // "Get Started" signup entry point (AccountModal in signup mode).
  const [showRegistration, setShowRegistration] = useState(false);
  useEffect(() => {
    getSessionUser().then(setAccountUser);
  }, [setAccountUser]);

  // A signed-in visitor who reloads the page (or returns via a bookmark) lands back on the
  // landing page with no URL routing to tell us otherwise. Rather than making them click
  // "Client Login" again to trigger the very same session check, do it once automatically —
  // mirrors exactly what AccountModal does on open: if a live session exists, resume straight
  // into the last page/step the server has saved. Skips when a more specific entry flow (invite
  // link, password reset, email-confirmation return, Stripe return) already owns this load, and
  // never fires more than once per app load. Declared BEFORE those flows' own accountUser-effects
  // so it reads their ref flags (below) before those effects clear them.
  const autoResumeAttemptedRef = useRef(false);
  useEffect(() => {
    if (!accountUser || autoResumeAttemptedRef.current) return;
    if (activePage !== 'LandingPage') return;
    if (resetToken || confirmReturnRef.current || billingReturnRef.current || getPendingInvite() || inviteInProgress()) return;
    autoResumeAttemptedRef.current = true;
    (async () => {
      const loaded = await loadLatestClientProfile().catch(() => null);
      if (loaded) setFactFind(loaded.factFind);
      resumeFromSignIn(loaded);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accountUser]);

  // LEGACY: this once auto-reopened the wizard for the old email-CONFIRMATION-LINK round trip.
  // Sign-up is now a single-screen code (no link/second tab), so `awaitingAuth` is never set by
  // current code — and a STALE draft left in localStorage from an earlier attempt must NOT pop
  // the "Become a partner" wizard over a normal sign-in. So we clear any such stale draft here
  // instead of acting on it.
  useEffect(() => {
    if (getPartnerWizardDraft()?.awaitingAuth) clearPartnerWizardDraft();
  }, []);

  // Arriving via an email-confirmation link: don't leave the new user stranded on the landing
  // page — continue their journey (partner wizard if that's what they were doing, otherwise
  // straight into the client flow).
  const confirmReturnRef = useRef(false);
  useEffect(() => {
    if (/type=signup/.test(window.location.hash)) confirmReturnRef.current = true;
  }, []);

  // Returning from Stripe hosted Checkout (?billing=success|cancelled). Capture + strip it on load.
  const billingReturnRef = useRef<'success' | 'cancelled' | null>(null);
  useEffect(() => {
    billingReturnRef.current = consumeBillingReturn();
  }, []);
  // Once signed in, act on the Stripe return: success → re-check access and enter the app;
  // cancelled → keep them on the paywall (they're signed in but haven't paid).
  useEffect(() => {
    if (!accountUser || !billingReturnRef.current) return;
    const outcome = billingReturnRef.current;
    billingReturnRef.current = null;
    if (outcome === 'success') {
      // A partner may be returning from a plan/slot purchase — clear the setup chooser and the
      // "add another client" modal so the portal-entry gate re-reads their (now paid) summary.
      setPartnerPaywall(null);
      setAddClientPaywall(null);
      (async () => {
        const loaded = await loadLatestClientProfile().catch(() => null);
        if (loaded) setFactFind(loaded.factFind);
        resumeFromSignIn(loaded);
      })();
    } else if (outcome === 'cancelled') {
      setShowPaywall(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accountUser]);

  // A password-reset email link returns the user to the app (?reset=<token>) — open "choose a
  // new password" with the token threaded into AccountModal for the confirm call.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const token = params.get('reset');
    if (!token) return;
    setResetToken(token);
    setAccountModalMode('reset');
    setShowAccountModal(true);
  }, []);

  // A person clicking an invite link deserves to KNOW what happened — never silence.
  const [inviteNotice, setInviteNotice] = useState<{ tone: 'success' | 'error'; text: string } | null>(null);
  useEffect(() => {
    if (!inviteNotice) return undefined;
    const t = setTimeout(() => setInviteNotice(null), 12000);
    return () => clearTimeout(t);
  }, [inviteNotice]);

  // Invite links (?invite=TOKEN): remember the token, and ask the visitor to sign in / create
  // an account — the invite is redeemed right after they're signed in.
  useEffect(() => {
    const token = capturePendingInviteFromUrl();
    if (!token) return;
    getSessionUser().then(async (user) => {
      if (user) {
        // Taken, not read: whichever handler takes it redeems it, and only that one.
        const taken = takePendingInvite();
        if (!taken) return;
        const res = await acceptInvite(taken).finally(() => finishInviteRedemption());
        if (!res.ok) {
          setInviteNotice({
            tone: 'error',
            text: /different email/i.test(res.message)
            // The usual way here: an adviser testing their own invite in the browser they are signed in to.
            ? "This invitation was sent to a different email address than the one you're signed in with. Sign out, then open the link again."
            : `That invitation link could not be used: ${res.message}. Please ask whoever sent it for a fresh one.`,
          });
          return;
        }
        if (['owner', 'adviser', 'staff'].includes(res.role)) {
          setPartnerResolving(true);
          setActivePage('PartnerPortal');
          return;
        }
        // Client invite: open the file their adviser prepared and tell them it worked.
        const loaded = await loadLatestClientProfile().catch(() => null);
        if (loaded) setFactFind(loaded.factFind);
        setInviteNotice({ tone: 'success', text: "You're connected — this plan is linked with your adviser." });
        setClientProfileStep(1);
        setActivePage('Client Profile');
      } else {
        // Most invitees are brand new — greet them with CREATE ACCOUNT, not "Welcome back".
        setAccountModalMode('signup');
        setShowAccountModal(true);
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * Consumer app-entry gate (Option A paywall). If billing is LIVE and this consumer has no active
   * access, show the paywall; otherwise run `proceed` (into their plan / fact-find). Safe while
   * billing is off: the server reports hasAccess:true, so `proceed` always runs and nothing changes.
   */
  const enterConsumerWithBilling = async (proceed: () => void) => {
    const ent = await getEntitlement();
    if (ent.billingConfigured && !ent.hasAccess) {
      setShowPaywall(true);
      return;
    }
    setShowPaywall(false);
    proceed();
  };

  /** Signed in → put the client back exactly where they left off (or the fact-find if brand new). */
  const resumeFromSignIn = async (loaded: LoadedClientProfile | null) => {
    // Drives auto-save (see autosaveEnabled below) — without this, a session that just signed in
    // this page load (rather than resuming an existing one at mount) never enables auto-save, so
    // nothing about the plan the client is about to fill in ever gets persisted.
    setAccountUser(await getSessionUser());
    closeAccountModal();
    setActiveProfessionalCaseId(null);
    setActiveRealProfileId(null);
    rearmAdviserIntro();

    // An invite link brought them here: join the firm/file first; partner staff go to the portal.
    const inviteToken = takePendingInvite();
    if (inviteToken) {
      const res = await acceptInvite(inviteToken).finally(() => finishInviteRedemption());
      if (!res.ok) {
        // Say so — a silently-failed invite reads as "the app is broken".
        setInviteNotice({
          tone: 'error',
          text: `That invitation link could not be used: ${res.message}. Please ask whoever sent it for a fresh one. (You are signed in; your own plan is still here.)`,
        });
      }
      if (res.ok && ['owner', 'adviser', 'staff'].includes(res.role)) {
        setPartnerResolving(true); // show the branded loader, never the demo login, from frame 1
        setActivePage('PartnerPortal');
        return;
      }
      // A client invite just linked their prepared file — load it fresh (the earlier lookup
      // ran before the acceptance, so it saw nothing), and start them on step 1 of the profile:
      // the file is a shell their adviser named, not a plan to resume (owner, 16 Oct 2026 — an
      // invited client landed on a dashboard that called their empty profile complete).
      if (res.ok && res.role === 'client') {
        loaded = await loadLatestClientProfile().catch(() => null);
        if (loaded) setFactFind(loaded.factFind);
        setInviteNotice({ tone: 'success', text: "You're connected — this plan is linked with your adviser." });
        setClientProfileStep(1);
        setActivePage('Client Profile');
        return;
      }
    }

    // Signed in through the partner door: firm members go to the portal. Only offer the
    // onboarding wizard when we POSITIVELY confirm they have no firm — a transient failure must
    // never mean "become a partner" for a returning owner.
    // The recorded door counts as well as the in-memory flag. closeAccountModal() above clears
    // the flag, and setting the session starts a SECOND, racing copy of this handler via the
    // auto-resume effect — that copy saw no flag, took the consumer path and overwrote the
    // workspace. It also means a reload keeps a partner in their workspace. See signInDoor.ts.
    if (partnerIntent || getSignInDoor() === 'partner') {
      setPartnerIntent(false);
      const res = await resolveFirmMembershipStatus();
      if (res.membership) {
        clearPartnerWizardDraft(); // never let a stale signup draft resurface for a real member
        setPartnerResolving(true); // show the branded loader, never the demo login, from frame 1
        setActivePage('PartnerPortal');
      } else if (res.ok) {
        // Confirmed: this login has no firm. A platform admin goes to the admin console; anyone
        // else is offered onboarding.
        if (!(await routePlatformAdminWithoutFirm())) setShowPartnerWizard(true);
      } else {
        // Couldn't determine (network/timeout) → send them to the portal, whose own entry effect
        // retries and shows a retry screen on continued failure. NEVER the signup wizard.
        setPartnerResolving(true);
        setActivePage('PartnerPortal');
      }
      return;
    }

    // Not flagged as a partner sign-in, but this login may still belong to a firm — e.g. a partner
    // returning via a password-reset link (no partner-door flag). A firm member's home is their
    // portal (client list), never the consumer fact-find. (On a "couldn't determine" result we fall
    // through to the consumer resume — least surprising for a non-partner-door login.)
    const firm = await resolveFirmMembershipStatus();
    if (firm.membership) {
      clearPartnerWizardDraft();
      setPartnerResolving(true); // branded loader, never the demo login, from frame 1
      setActivePage('PartnerPortal');
      return;
    }

    // A platform admin's home is the firms console, whichever door they used (owner, 1 Oct 2026):
    // signing in through the client door used to land the owner on Client Profile step 1.
    if (firm.ok && (await routePlatformAdminWithoutFirm())) return;

    if (!loaded) {
      // No saved plan on this account yet — start the fact-find (behind the paywall if billing is live).
      await enterConsumerWithBilling(() => {
        setClientProfileStep(1);
        setActivePage('Client Profile');
      });
      return;
    }
    // Put back everything the account holds: strategies, scenarios, SOA case studies,
    // service plans — then land the user on the screen they last worked on.
    const appData: ProfileAppData = loaded.appData || {};
    if (Array.isArray(appData.savedStrategies)) setSavedStrategies(appData.savedStrategies as SavedStrategy[]);
    if (Array.isArray(appData.savedCaseStudies)) setSavedCaseStudies(appData.savedCaseStudies as SavedCaseStudy[]);
    if (appData.coupleValidation) setCoupleValidation(appData.coupleValidation as CoupleValidationState);
    restoreSessionMirror(appData);

    const appState: ProfileAppState = loaded.appState || {};
    if (appState.clientProfileStep) setClientProfileStep(Number(appState.clientProfileStep) || 1);
    const lastPageRaw = appState.lastPage;
    const lastPage = typeof lastPageRaw === 'string' && RESUMABLE_PAGES.includes(lastPageRaw) ? lastPageRaw : 'Dashboard';
    await enterConsumerWithBilling(() => setActivePage(lastPage));
  };

  // Completes the email-confirmation arrival once the session is ready (see confirmReturnRef).
  useEffect(() => {
    if (!confirmReturnRef.current || !accountUser) return;
    confirmReturnRef.current = false;
    // A partner mid-wizard owns this arrival — never hijack it into the client flow.
    if (showPartnerWizard || getPartnerWizardDraft()?.awaitingAuth) return;
    (async () => {
      const loaded = await loadLatestClientProfile().catch(() => null);
      if (loaded) setFactFind(loaded.factFind);
      resumeFromSignIn(loaded);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accountUser]);

  /** Signed out → clear their data from the screen and this browser, back to the front door. */
  const handleSignedOut = async () => {
    // Wait for any in-flight/queued autosave to actually land BEFORE invalidating the session —
    // authSignOut() below revokes the token, so a save still in flight (or one about to fire)
    // would fail auth and be silently dropped, losing the last few seconds of edits even though
    // an earlier, less complete autosave already succeeded. See useAutoSave.ts's doc comment.
    await flushPendingSave().catch(() => {});
    // Always end the real session BEFORE navigating (awaited), so a subsequent login
    // can never see a stale session. The portal's Sign out calls this directly (bypassing the
    // account modal), so the sign-out must live here.
    await authSignOut().catch(() => {});
    // Forget whose licence advice is given under. Without this, a second firm signing in on the same
    // tab would generate documents carrying the FIRST firm's attribution until their workspace
    // finished resolving — the wrong licensee on a client document, for exactly as long as nobody
    // noticed.
    setCurrentAdviceAttribution(null);
    closeAccountModal();
    setFactFind(structuredClone(INITIAL_FACT_FIND));
    setSavedStrategies([]);
    setSavedCaseStudies([]);
    clearSessionMirror();
    setStoredProfileId(null); // forget which cloud file this browser was writing to
    setActiveProfessionalCaseId(null);
    setActiveRealProfileId(null);
    setPartnerFirm(null);
    setPartnerIntent(false); // a cancelled/finished partner journey must never haunt the next login
    clearSignInDoor(); // ...and neither must the door it came through
    setAccountUser(null);
    setClientProfileStep(1);
    setActivePage('LandingPage');
  };

  const closeAccountModal = () => {
    setShowAccountModal(false);
    setAccountModalMode(null);
    // A partner sign-in that didn't finish must not haunt the next journey: without this, a later
    // "Client Login" shows partner branding and shoves a plain consumer into the firm wizard.
    // (Safe on success too: the in-flight resume still sees its own copy of the flag.)
    setPartnerIntent(false);
    // Re-check the portal after the security panel closes (an MFA enrolment upgrades the
    // session, so the firm's client list can now load).
    if (activePage === 'PartnerPortal') setPortalRefreshNonce((n) => n + 1);
  };

  return {
    resetToken,
    accountModalMode,
    showAccountModal,
    setShowAccountModal,
    showRegistration,
    setShowRegistration,
    inviteNotice,
    setInviteNotice,
    enterConsumerWithBilling,
    resumeFromSignIn,
    handleSignedOut,
    closeAccountModal,
  };
}
